import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveJobRecommendation } from '@worker/services/MyJobs/api';
import {
  WorkerJobCommonResultDTO,
  WorkerJobCommonResultsResponse,
} from '@worker/types/myJobs.type';
import {
  updateWorkerJobSavedState,
  extractErrorMessage,
} from '@worker/helpers/my-jobs-helpers';
import QueryKeys from '@constants/queryKeys';
import useNotification from '@hooks/useNotification';

/**
 * Hook to save a job recommendation
 * Updates cache on success only - no optimistic updates or query invalidation
 */
export function useSaveJobMutation() {
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  return useMutation({
    mutationKey: [QueryKeys.MyJobs.SAVE_JOB],
    mutationFn: (jobId: number) => saveJobRecommendation(jobId),
    onSuccess: (response, jobId) => {
      // Update ALL job recommendation caches
      queryClient.setQueriesData<WorkerJobCommonResultsResponse>(
        { queryKey: [QueryKeys.MyJobs.JOB_RECOMMENDATIONS] },
        (data) => updateWorkerJobSavedState(data, jobId, response.savedAt)
      );

      queryClient.setQueriesData<WorkerJobCommonResultDTO>(
        { queryKey: [QueryKeys.MyJobs.JOB_RECOMMENDATION_BY_ID] },
        (data) => {
          if (!data || data.job?.id !== jobId) return data;
          return {
            ...data,
            workerSavedJob: {
              savedJobId: data.workerSavedJob?.savedJobId || 0,
              jobId,
              savedAt: response.savedAt,
            },
          };
        }
      );

      showSuccessNotification('Job saved!', { title: 'Saved' });
    },
    onError: (err: unknown) => {
      // No rollback needed - cache was never updated
      showErrorNotification(
        extractErrorMessage(err, 'Failed to save job. Please try again.')
      );
    },
  });
}

export default useSaveJobMutation;
